import React from 'react';

// Shared AI-theme building blocks for the resume builder.
// The builder is always dark ("AI studio" surface) and uses the app's brand
// grid — secondary/purple (#7C3AED) + primary/blue (#2563EB). Motion is kept
// subtle: the background is static and gradient borders don't animate.

export const rb = {
  pageBg: 'bg-[#08070f] text-slate-100 min-h-screen',
  glass: 'rb-glass',
  glassSoft: 'rb-glass-soft',
  gradRing: 'rb-grad-ring',
  gradText: 'rb-grad-text',
  glowBtn: 'rb-glow-btn font-bold disabled:opacity-50 disabled:cursor-not-allowed',
  secondaryBtn:
    'bg-white/5 hover:bg-white/10 border border-white/10 text-slate-100 font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors',
  input: 'rb-input',
  text: 'text-slate-100',
  subtext: 'text-slate-400',
  chip: 'bg-white/5 border border-white/10 text-slate-200',
  accent: 'text-violet-300',
};

// Static, subtle aurora backdrop (on-brand purple → blue glows).
export function AIBackground() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden bg-[#08070f]">
      <div
        className="absolute -top-40 -left-32 w-[620px] h-[620px] rounded-full opacity-40"
        style={{ background: 'radial-gradient(circle, #7C3AED, transparent 70%)', filter: 'blur(120px)' }}
      />
      <div
        className="absolute top-1/3 left-1/2 w-[440px] h-[440px] rounded-full opacity-25"
        style={{ background: 'radial-gradient(circle, #8B5CF6, transparent 70%)', filter: 'blur(120px)' }}
      />
      <div
        className="absolute -bottom-48 -right-24 w-[580px] h-[580px] rounded-full opacity-30"
        style={{ background: 'radial-gradient(circle, #2563EB, transparent 70%)', filter: 'blur(120px)' }}
      />
    </div>
  );
}

export function RBField({ label, hint, children }) {
  return (
    <label className="block">
      {label && <span className="block text-xs font-semibold text-slate-300 mb-1.5">{label}</span>}
      {children}
      {hint && <span className="block text-[11px] text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}

export function RBInput(props) {
  return <input {...props} className={`w-full px-3.5 py-2.5 rounded-xl text-sm rb-input ${props.className || ''}`} />;
}

export function RBTextArea(props) {
  return <textarea {...props} className={`w-full px-3.5 py-2.5 rounded-xl text-sm rb-input resize-none ${props.className || ''}`} />;
}

// Chip / tag input styled for the dark AI theme.
export function RBChipInput({ values = [], onChange, suggestions = [], placeholder }) {
  const [input, setInput] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const addValue = (raw) => {
    const value = raw.trim();
    if (!value) return;
    if (!values.some((v) => v.toLowerCase() === value.toLowerCase())) onChange([...values, value]);
    setInput('');
  };

  const matches = suggestions
    .filter((s) => s.toLowerCase().includes(input.toLowerCase()) && !values.some((v) => v.toLowerCase() === s.toLowerCase()))
    .slice(0, 8);

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex flex-wrap items-center gap-2 rounded-xl px-2 py-2 rb-input">
        {values.map((v) => (
          <span key={v} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-violet-500/20 text-violet-200 border border-violet-400/20">
            {v}
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} aria-label={`Remove ${v}`} className="opacity-70 hover:opacity-100">×</button>
          </span>
        ))}
        <input
          value={input}
          placeholder={values.length === 0 ? placeholder : ''}
          onChange={(e) => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addValue(input); }
            else if (e.key === 'Backspace' && !input && values.length) onChange(values.slice(0, -1));
          }}
          className="flex-1 min-w-[120px] bg-transparent px-1 py-1 text-sm focus:outline-none text-slate-100 placeholder-slate-500"
        />
      </div>
      {open && matches.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full rounded-xl overflow-hidden rb-glass max-h-48 overflow-y-auto">
          {matches.map((s) => (
            <li key={s}>
              <button type="button" onClick={() => { addValue(s); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-white/10">
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function RBAddButton({ label, onClick }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-300 hover:text-violet-200">
      <span className="text-base leading-none">+</span> {label}
    </button>
  );
}

export function RBEmptyHint({ children }) {
  return (
    <div className="rounded-xl border border-dashed border-white/10 px-4 py-5 text-center text-sm text-slate-500">
      {children}
    </div>
  );
}
